import { AuthProvider } from "@refinedev/core";
import { AuthError } from "@supabase/supabase-js";
import { supabaseClient } from "./supabase-client";

// Panel yalnız etkin yönetici hesabına açık: role = 'admin', pasif değil,
// silinmemiş. Veritabanındaki is_crm_admin() ile aynı kural (F7.3).
type AdminCheck = "ok" | "not_admin" | "inactive";
const checkAdmin = async (userId: string): Promise<AdminCheck> => {
  const { data } = await supabaseClient
    .from("profiles")
    .select("role, is_active, deleted_at")
    .eq("id", userId)
    .maybeSingle();
  if (data?.role !== "admin" || data.deleted_at) return "not_admin";
  if (data.is_active === false) return "inactive";
  return "ok";
};

const toAuthError = (error: unknown) => {
  if (error instanceof AuthError) {
    return { message: error.message, name: error.name };
  }
  if (error instanceof Error) {
    return { message: error.message, name: error.name };
  }
  return { message: "Beklenmeyen hata.", name: "Hata" };
};

const ACCESS_DENIED = {
  message: "Bu panele erişim yetkiniz yok.",
  name: "Yetkisiz Erişim",
};

const INACTIVE = {
  message: "Bu yönetici hesabı pasife alındı. Kendi hesabınla giriş yap.",
  name: "Hesap pasif",
};

const deniedError = (c: AdminCheck) => (c === "inactive" ? INACTIVE : ACCESS_DENIED);

const authProvider: AuthProvider = {
  login: async ({ email, password, providerName }) => {
    try {
      if (providerName) {
        const { data, error } = await supabaseClient.auth.signInWithOAuth({
          provider: providerName,
        });
        if (error) return { success: false, error };
        if (data?.url) return { success: true, redirectTo: "/hub" };
      }

      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) return { success: false, error };

      if (data?.user) {
        // Yönetici kontrolü — profiles tablosundan rol ve durum sorgulanır
        const admin = await checkAdmin(data.user.id);

        if (admin !== "ok") {
          // Yönetici değilse ya da hesap pasifse oturumu hemen kapat
          await supabaseClient.auth.signOut();
          return {
            success: false,
            error: deniedError(admin),
          };
        }

        return { success: true, redirectTo: "/hub" };
      }
    } catch (error) {
      return { success: false, error: toAuthError(error) };
    }

    return {
      success: false,
      error: { message: "Giriş başarısız.", name: "Geçersiz e-posta veya şifre" },
    };
  },

  // Kayıt devre dışı — panele sadece mevcut admin hesaplarla girilir.
  register: async () => {
    return {
      success: false,
      error: {
        message: "Bu panelde yeni hesap oluşturulamaz.",
        name: "Kayıt Devre Dışı",
      },
    };
  },

  forgotPassword: async ({ email }) => {
    try {
      const { data, error } = await supabaseClient.auth.resetPasswordForEmail(
        email,
        { redirectTo: `${window.location.origin}/update-password` }
      );
      if (error) return { success: false, error };
      if (data) return { success: true };
    } catch (error) {
      return { success: false, error: toAuthError(error) };
    }
    return {
      success: false,
      error: { message: "Şifre sıfırlama başarısız.", name: "Geçersiz e-posta" },
    };
  },

  updatePassword: async ({ password }) => {
    try {
      const { data, error } = await supabaseClient.auth.updateUser({ password });
      if (error) return { success: false, error };
      if (data) return { success: true, redirectTo: "/" };
    } catch (error) {
      return { success: false, error: toAuthError(error) };
    }
    return {
      success: false,
      error: { message: "Şifre güncelleme başarısız.", name: "Geçersiz şifre" },
    };
  },

  logout: async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) return { success: false, error };
    return { success: true, redirectTo: "/login" };
  },

  onError: async (error) => {
    return { error };
  },

  check: async () => {
    try {
      const { data } = await supabaseClient.auth.getSession();
      const { session } = data;

      if (!session) {
        return {
          authenticated: false,
          error: { message: "Oturum bulunamadı.", name: "Oturum Yok" },
          logout: true,
          redirectTo: "/login",
        };
      }

      // Her sayfa geçişinde yönetici rolü ve hesap durumu tekrar doğrulanır
      // (pasife alınan hesabın açık oturumu burada kapanır)
      const admin = await checkAdmin(session.user.id);
      if (admin !== "ok") {
        await supabaseClient.auth.signOut();
        return {
          authenticated: false,
          error: deniedError(admin),
          logout: true,
          redirectTo: "/login",
        };
      }

      return { authenticated: true };
    } catch (error) {
      return {
        authenticated: false,
        error: toAuthError(error),
        logout: true,
        redirectTo: "/login",
      };
    }
  },

  getPermissions: async () => {
    const { data } = await supabaseClient.auth.getUser();
    if (!data?.user) return null;
    const { data: profile } = await supabaseClient
      .from("profiles")
      .select("role, crm_scopes")
      .eq("id", data.user.id)
      .maybeSingle();
    if (!profile) return null;
    // { role, scopes }: scopes boş = tam yetki (FAZ D RBAC).
    return {
      role: profile.role ?? null,
      scopes: (profile.crm_scopes as string[] | null) ?? [],
    };
  },

  getIdentity: async () => {
    const { data } = await supabaseClient.auth.getUser();
    if (!data?.user) return null;
    // Ad ve avatar profilden ("Günaydın, Atakan"); profil okunamazsa e-posta.
    const { data: profile } = await supabaseClient
      .from("profiles")
      .select("full_name, avatar_url")
      .eq("id", data.user.id)
      .maybeSingle();
    return {
      ...data.user,
      name: profile?.full_name?.trim() || data.user.email,
      avatar: profile?.avatar_url ?? undefined,
    };
  },
};

export default authProvider;
